import React,{useEffect,useRef,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import DisputeEvidencePanel from './DisputeEvidencePanel';
import {
  fetchOrderDispute,
  reportDeliveryIssue
} from '../../services/deliveryConfirmationService';

const {FiAlertTriangle,FiLoader,FiUpload}=FiIcons;

const statusCopy={
  open:'Your dispute is open and waiting for review.',
  investigating:'Your dispute is being investigated.',
  resolved:'Your dispute has been resolved.',
  rejected:'Your dispute was rejected.'
};

const statusClass={
  open:'border-amber-100 bg-amber-50 text-amber-800',
  investigating:'border-indigo-100 bg-indigo-50 text-indigo-800',
  resolved:'border-emerald-100 bg-emerald-50 text-emerald-800',
  rejected:'border-slate-200 bg-slate-100 text-slate-700'
};

const DeliveryIssueReportButton=({order,onReported})=>{
  const [isOpen,setIsOpen]=useState(false);
  const [description,setDescription]=useState('');
  const [files,setFiles]=useState([]);
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState(false);
  const [dispute,setDispute]=useState(null);
  const inputRef=useRef(null);

  useEffect(()=>{
    let active=true;

    if (!order?.id) return undefined;

    fetchOrderDispute(order.id)
      .then((result)=>{
        if (active) setDispute(result);
      })
      .catch((requestError)=>{
        if (active) setError(requestError.message || 'Unable to load dispute status.');
      });

    return ()=> {
      active=false;
    };
  },[order?.id]);

  if (!order || order.delivery_method!=='delivery' || (order.status!=='ready' && !dispute)) {
    return null;
  }

  const handleSubmit=async(event)=>{
    event.preventDefault();
    event.stopPropagation();

    if (description.trim().length<10) {
      setError('Please describe the issue in at least 10 characters.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const createdDispute=await reportDeliveryIssue(order.id,description,files);
      setDescription('');
      setFiles([]);
      setDispute(createdDispute);
      setSuccess(true);
      setIsOpen(false);
      window.dispatchEvent(new Event('sohlocal:orders-changed'));
      await onReported?.(createdDispute);
    } catch (requestError) {
      setError(requestError.message || 'Unable to report the issue.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isClosed=['resolved','rejected'].includes(dispute?.status);

  return (
    <div className="w-full sm:w-auto">
      {dispute && (
        <>
          <div className={`mb-3 rounded-2xl border p-4 text-xs font-bold ${statusClass[dispute.status] || statusClass.open}`}>
            <p className="font-black uppercase tracking-widest">
              Dispute submitted
            </p>
            <p className="mt-1">
              {statusCopy[dispute.status] || 'Your dispute is in progress.'}
            </p>
            {isClosed && (
              <div className="mt-3 border-t border-current/15 pt-3">
                <p className="font-black uppercase tracking-widest">
                  Resolution result
                </p>
                <p className="mt-1 font-black">
                  {dispute.status==='resolved' ? 'Resolved' : 'Rejected'}
                  {' · '}
                  {Number(dispute.refund_amount || 0)>0
                    ? `Refund issued: $${Number(dispute.refund_amount).toFixed(2)}`
                    : 'No refund issued'}
                </p>
                {dispute.resolution_note && (
                  <p className="mt-2 whitespace-pre-wrap font-medium">
                    {dispute.resolution_note}
                  </p>
                )}
                {dispute.stripe_refund_id && (
                  <p className="mt-2 break-all text-[10px]">
                    Stripe refund: {dispute.stripe_refund_id}
                  </p>
                )}
              </div>
            )}
          </div>
          <DisputeEvidencePanel
            disputeId={dispute.id}
            orderId={order.id}
          />
        </>
      )}

      {success && !dispute && (
        <p className="mt-2 text-xs font-bold text-emerald-600">
          Issue reported. Our team will review it.
        </p>
      )}

      {!dispute && order.status==='ready' && (
        <button
          type="button"
          onClick={(event)=>{
            event.stopPropagation();
            setIsOpen((value)=>!value);
            setSuccess(false);
            setError('');
          }}
          className="flex items-center justify-center rounded-xl border border-red-200 bg-white px-5 py-3 text-[10px] font-black uppercase tracking-widest text-red-600"
        >
          <SafeIcon icon={FiAlertTriangle} className="mr-2" />
          Report an Issue
        </button>
      )}

      {isOpen && order.status==='ready' && !dispute && (
        <form
          onSubmit={handleSubmit}
          onClick={(event)=>event.stopPropagation()}
          className="mt-3 w-full rounded-2xl border border-red-100 bg-white p-4 shadow-sm sm:w-80"
        >
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Describe the issue
          </label>
          <textarea
            value={description}
            onChange={(event)=>setDescription(event.target.value)}
            rows="4"
            placeholder="Tell us what is wrong with this delivery..."
            className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-red-300"
          />

          <button
            type="button"
            onClick={()=>inputRef.current?.click()}
            className="mt-3 flex w-full items-center justify-center rounded-xl border border-slate-200 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600"
          >
            <SafeIcon icon={FiUpload} className="mr-2" />
            {files.length ? `${files.length} image${files.length===1 ? '' : 's'} selected` : 'Attach evidence images'}
          </button>

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={(event)=>setFiles(Array.from(event.target.files || []))}
            className="hidden"
          />

          {error && (
            <p className="mt-2 text-xs font-bold text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-3 flex w-full items-center justify-center rounded-xl bg-red-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
          >
            {isSubmitting && (
              <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
            )}
            Submit Issue
          </button>
        </form>
      )}
    </div>
  );
};

export default DeliveryIssueReportButton;
